import React, { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Button, Spinner, Tabs, Tab, Form } from 'react-bootstrap';
import DesktopLayout from '../layouts/DesktopLayout';
import StockItemTile, { tileState } from '../components/stock/StockItemTile';
import StockEntrySheet from '../components/stock/StockEntrySheet';
import StockItemModal from '../components/stock/StockItemModal';
import AddStockItemModal from '../components/stock/AddStockItemModal';
import StockPeopleTab from '../components/stock/StockPeopleTab';
import { useStockOptions, useStockStores, useStockSummary, useCreateStore } from '../hooks/useStock';
import { formatCount } from '../utils/numberFormatter';
import { TEXT, ENTRY_ACTIONS, errorText } from '../config/stockLabels';
import '../styles/StockPage.css';

/**
 * «المخزون» — the store count of guest supplies (tasks/stock.md step 4). Ops only (O5).
 *
 * Every figure, flag and count on this page is the server's: quantities are summed from the history,
 * "low", "count is wrong" and "recount overdue" are decided there (D9, D10). The page orders the
 * tiles so problems come first, and nothing else.
 *
 * Two tabs, the open one in the URL (`?view=people`): «الأصناف» (the shelf) and «حسب الموظف» (the
 * per-person check, D12). Stock moves only through the three forms in the action bar (D6, D8).
 *
 * Arabic-only, like every ops page.
 */
const STATE_ORDER = { negative: 0, low: 1, ok: 2, archived: 3 };

const CreateStoreCard = () => {
  const createStore = useCreateStore();
  const [name, setName] = useState(TEXT.storeNameDefault);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await createStore.mutateAsync({ name });
    } catch (err) {
      setError(errorText(err));
    }
  };

  return (
    <form className="stk-start" onSubmit={submit}>
      <i className="bi bi-shop" aria-hidden="true"></i>
      <h3 className="stk-start__title">{TEXT.noStoreTitle}</h3>
      <p className="stk-muted">{TEXT.noStoreHint}</p>
      <Form.Group className="stk-field stk-start__field" controlId="stk-store-name">
        <Form.Label>{TEXT.storeNameLabel}</Form.Label>
        <Form.Control value={name} maxLength={80} onChange={(e) => setName(e.target.value)} />
      </Form.Group>
      {error && <div className="stk-error" role="alert">{error}</div>}
      <Button type="submit" variant="primary" disabled={createStore.isPending || !name.trim()}>
        {createStore.isPending && <Spinner animation="border" size="sm" className="stk-spin" />}
        {TEXT.createStore}
      </Button>
    </form>
  );
};

const StockPage = () => {
  const { data: options } = useStockOptions();
  // "No store" and "store is empty" are said only once the server has ANSWERED. A request still waiting —
  // including a retry paused while the tab is in the background or the server is down — shows loading,
  // never a first-run or empty screen (React Query: `isPending` = no answer yet; `isLoading` would be
  // false during a paused retry).
  const { data: stores, isPending: storesPending, isError: storesError, isSuccess: storesAnswered, refetch: refetchStores } = useStockStores();
  // Phase 1 has one store (D4); the page works on the first.
  const store = stores?.[0] || null;

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('view') === 'people' ? 'people' : 'items';
  const [showArchived, setShowArchived] = useState(false);
  const { summary, isPending, isError, isSuccess, refetch, isFetching } = useStockSummary(store?.id, showArchived);

  const [sheet, setSheet] = useState(null);           // 'purchase' | 'takeOut' | 'recount'
  const [adding, setAdding] = useState(false);
  const [openItemId, setOpenItemId] = useState(null);
  const [period, setPeriod] = useState(null);

  const activeItems = useMemo(() => summary.items.filter(i => !i.archived), [summary.items]);
  // Problems first; the server's name order within each state.
  const tiles = useMemo(
    () => summary.items
      .map((item, index) => ({ item, index }))
      .sort((a, b) => (STATE_ORDER[tileState(a.item)] - STATE_ORDER[tileState(b.item)]) || (a.index - b.index))
      .map(({ item }) => item),
    [summary.items]
  );
  // The open item is read from the live list, so its count follows every refresh.
  const openItem = openItemId ? summary.items.find(i => String(i.id) === String(openItemId)) || null : null;

  const ready = Boolean(store) && isSuccess;
  const { counts } = summary;
  const overdueDays = options?.recountOverdueDays ?? summary.recountOverdueDays;

  // The list is read only once a store exists — refreshing before that asks for the store alone.
  const refresh = () => { refetchStores(); if (store) refetch(); };

  return (
    <DesktopLayout activeNavItem="stock" headerTitle={TEXT.pageTitle}>
      <Container fluid className="stk-page" dir="rtl">
        <div className="stk-page-head">
          <div>
            <h2 className="stk-page-title">{TEXT.pageTitle}</h2>
            <p className="stk-page-subtitle">{store ? store.name : TEXT.pageSubtitle}</p>
          </div>
          <Button variant="link" className="stk-link" onClick={refresh} disabled={isFetching}>
            <i className="bi bi-arrow-clockwise" aria-hidden="true"></i> {TEXT.refresh}
          </Button>
        </div>

        {((storesPending && !storesError) || (store && isPending && !isError)) && (
          <div className="stk-state"><Spinner animation="border" size="sm" /> {TEXT.loading}</div>
        )}
        {(storesError || isError) && (
          <div className="stk-error" role="alert">
            {TEXT.loadError} <Button variant="link" className="stk-link" onClick={refresh}>{TEXT.refresh}</Button>
          </div>
        )}

        {storesAnswered && !store && <CreateStoreCard />}

        {ready && (
          <>
            <div className="stk-actions" role="toolbar" aria-label={TEXT.pageTitle}>
              <div className="stk-actions__main">
                {['takeOut', 'purchase', 'recount'].map(kind => (
                  <Button
                    key={kind}
                    variant={kind === 'takeOut' ? 'primary' : 'outline-primary'}
                    className="stk-action"
                    onClick={() => setSheet(kind)}
                    disabled={activeItems.length === 0}
                  >
                    <i className={`bi ${ENTRY_ACTIONS[kind].icon}`} aria-hidden="true"></i> {ENTRY_ACTIONS[kind].title}
                  </Button>
                ))}
              </div>
              <Button variant="outline-secondary" className="stk-action" onClick={() => setAdding(true)}>
                <i className="bi bi-plus-lg" aria-hidden="true"></i> {TEXT.addItem}
              </Button>
            </div>

            {counts.negative > 0 && (
              <div className="stk-banner stk-banner--danger" role="alert">
                <i className="bi bi-exclamation-octagon" aria-hidden="true"></i>
                <span>{TEXT.negativeBanner(counts.negative)}</span>
                <Button size="sm" variant="outline-danger" onClick={() => setSheet('recount')}>{TEXT.startRecount}</Button>
              </div>
            )}
            {counts.recountOverdue > 0 && (
              <div className="stk-banner stk-banner--warn" role="status">
                <i className="bi bi-clock-history" aria-hidden="true"></i>
                <span>{TEXT.overdueBanner(counts.recountOverdue, overdueDays)}</span>
                <Button size="sm" variant="outline-dark" onClick={() => setSheet('recount')}>{TEXT.startRecount}</Button>
              </div>
            )}

            <Tabs
              activeKey={activeTab}
              onSelect={(key) => key !== activeTab && setSearchParams(key === 'items' ? {} : { view: key })}
              className="stk-tabs"
            >
              <Tab eventKey="items" title={TEXT.tabItems}>
                <div className="stk-bar">
                  <div className="stk-counts" aria-live="polite">
                    <span className="stk-count"><strong>{formatCount(activeItems.length)}</strong>{TEXT.countItems}</span>
                    <span className="stk-count stk-count--low"><strong>{formatCount(counts.low)}</strong>{TEXT.countLow}</span>
                    <span className="stk-count stk-count--danger"><strong>{formatCount(counts.negative)}</strong>{TEXT.countNegative}</span>
                    <span className="stk-count"><strong>{formatCount(counts.recountOverdue)}</strong>{TEXT.countOverdue}</span>
                  </div>
                  <Form.Check
                    type="switch"
                    id="stk-show-archived"
                    className="stk-archived-toggle"
                    label={TEXT.showArchived}
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                  />
                </div>

                {tiles.length === 0 ? (
                  <div className="stk-empty">
                    <i className="bi bi-box-seam" aria-hidden="true"></i>
                    <p className="stk-empty__title">{TEXT.emptyTitle}</p>
                    <p className="stk-muted">{TEXT.emptyHint}</p>
                    <Button variant="primary" onClick={() => setAdding(true)}>
                      <i className="bi bi-plus-lg" aria-hidden="true"></i> {TEXT.addItem}
                    </Button>
                  </div>
                ) : (
                  <div className="stk-grid">
                    {tiles.map(item => <StockItemTile key={item.id} item={item} onOpen={(it) => setOpenItemId(it.id)} />)}
                  </div>
                )}
              </Tab>

              <Tab eventKey="people" title={TEXT.tabPeople}>
                {activeTab === 'people' && (
                  <StockPeopleTab
                    storeId={store.id}
                    presets={options?.periodPresets}
                    period={period || options?.defaultPeriod || 'thisMonth'}
                    onPeriod={setPeriod}
                  />
                )}
              </Tab>
            </Tabs>

            <StockEntrySheet
              kind={sheet || 'purchase'}
              show={Boolean(sheet)}
              onHide={() => setSheet(null)}
              storeId={store.id}
              items={activeItems}
            />
            <AddStockItemModal show={adding} onHide={() => setAdding(false)} storeId={store.id} units={options?.units} />
            <StockItemModal show={Boolean(openItem)} onHide={() => setOpenItemId(null)} storeId={store.id} item={openItem} />
          </>
        )}
      </Container>
    </DesktopLayout>
  );
};

export default StockPage;
